const DB_NAME = 'SkyChatDB';
const STORE_NAME = 'decryptedMedia';
const MAX_ENTRY_BYTES = 32 * 1024 * 1024;
const MAX_CACHE_BYTES = 192 * 1024 * 1024;
const MAX_CACHE_ENTRIES = 240;
const TOUCH_INTERVAL_MS = 6 * 60 * 60 * 1000;

interface StoredMediaRecord {
  id: string;
  blob: Blob;
  signature?: string;
  size?: number;
  createdAt?: number;
  lastAccessedAt?: number;
}

let dbPromise: Promise<IDBDatabase | null> | null = null;

function canUseIndexedDb(): boolean {
  return typeof indexedDB !== 'undefined';
}

function openMediaDb(): Promise<IDBDatabase | null> {
  if (!canUseIndexedDb()) return Promise.resolve(null);
  if (dbPromise) return dbPromise;

  dbPromise = new Promise((resolve) => {
    // Do not pin a version here. SkyChatDB is shared with the legacy client and
    // service worker, so opening the current version avoids VersionError when
    // another client has already upgraded the database.
    const request = indexedDB.open(DB_NAME);
    request.onerror = () => resolve(null);
    request.onblocked = () => resolve(null);
    request.onupgradeneeded = () => {
      if (!request.result.objectStoreNames.contains(STORE_NAME)) {
        request.result.createObjectStore(STORE_NAME, { keyPath: 'id' });
      }
    };
    request.onsuccess = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.close();
        dbPromise = null;
        resolve(null);
        return;
      }
      db.onversionchange = () => {
        db.close();
        dbPromise = null;
      };
      resolve(db);
    };
  });

  return dbPromise;
}

function deleteRecord(db: IDBDatabase, id: string): Promise<void> {
  return new Promise((resolve) => {
    const transaction = db.transaction(STORE_NAME, 'readwrite');
    transaction.objectStore(STORE_NAME).delete(id);
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => resolve();
    transaction.onabort = () => resolve();
  });
}

function writeRecord(db: IDBDatabase, record: StoredMediaRecord): Promise<void> {
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(STORE_NAME, 'readwrite');
    transaction.objectStore(STORE_NAME).put(record);
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error);
    transaction.onabort = () => reject(transaction.error);
  });
}

async function pruneMediaCache(db: IDBDatabase): Promise<void> {
  const entries = await new Promise<Array<{ id: string; size: number; lastAccessedAt: number }>>((resolve) => {
    const result: Array<{ id: string; size: number; lastAccessedAt: number }> = [];
    const transaction = db.transaction(STORE_NAME, 'readonly');
    const request = transaction.objectStore(STORE_NAME).openCursor();
    request.onsuccess = () => {
      const cursor = request.result;
      if (!cursor) return;
      const value = cursor.value as Partial<StoredMediaRecord>;
      if (typeof value.id === 'string') {
        result.push({
          id: value.id,
          size: Number(value.size || value.blob?.size || 0),
          lastAccessedAt: Number(value.lastAccessedAt || value.createdAt || 0)
        });
      }
      cursor.continue();
    };
    transaction.oncomplete = () => resolve(result);
    transaction.onerror = () => resolve(result);
    transaction.onabort = () => resolve(result);
  });

  let totalBytes = entries.reduce((sum, entry) => sum + entry.size, 0);
  let totalEntries = entries.length;
  if (totalBytes <= MAX_CACHE_BYTES && totalEntries <= MAX_CACHE_ENTRIES) return;

  const oldestFirst = [...entries].sort((a, b) => a.lastAccessedAt - b.lastAccessedAt);
  const idsToDelete: string[] = [];
  for (const entry of oldestFirst) {
    if (totalBytes <= MAX_CACHE_BYTES && totalEntries <= MAX_CACHE_ENTRIES) break;
    idsToDelete.push(entry.id);
    totalBytes -= entry.size;
    totalEntries -= 1;
  }
  if (!idsToDelete.length) return;

  await new Promise<void>((resolve) => {
    const transaction = db.transaction(STORE_NAME, 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    idsToDelete.forEach((id) => store.delete(id));
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => resolve();
    transaction.onabort = () => resolve();
  });
}

export async function getPersistentMedia(id: string, signature: string): Promise<Blob | null> {
  if (!id) return null;
  const db = await openMediaDb();
  if (!db) return null;

  const record = await new Promise<StoredMediaRecord | null>((resolve) => {
    const transaction = db.transaction(STORE_NAME, 'readonly');
    const request = transaction.objectStore(STORE_NAME).get(id);
    request.onsuccess = () => resolve((request.result as StoredMediaRecord | undefined) || null);
    request.onerror = () => resolve(null);
  });
  if (!record?.blob) return null;
  if (record.signature !== signature) {
    await deleteRecord(db, id);
    return null;
  }

  const now = Date.now();
  if (now - Number(record.lastAccessedAt || 0) >= TOUCH_INTERVAL_MS) {
    void writeRecord(db, {
      ...record,
      signature,
      size: record.blob.size,
      createdAt: Number(record.createdAt || now),
      lastAccessedAt: now
    }).catch(() => undefined);
  }
  return record.blob;
}

export async function savePersistentMedia(id: string, signature: string, blob: Blob): Promise<void> {
  if (!id || !blob || blob.size <= 0 || blob.size > MAX_ENTRY_BYTES) return;
  const db = await openMediaDb();
  if (!db) return;
  const now = Date.now();
  try {
    await writeRecord(db, {
      id,
      blob,
      signature,
      size: blob.size,
      createdAt: now,
      lastAccessedAt: now
    });
    await pruneMediaCache(db);
  } catch {
    // Storage quota and private-mode failures should not block media display.
  }
}

export async function deletePersistentMedia(id: string): Promise<void> {
  if (!id) return;
  const db = await openMediaDb();
  if (!db) return;
  await deleteRecord(db, id);
}
